import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  useArchiveClient,
  useClient,
  useClients,
  useCreateClient,
  useUpdateClient,
} from '../hooks/useApi';
import { CreateProjectModal } from '../components/CreateProjectModal';

/**
 * Página `/clients` — catálogo de clientes del workspace + CRUD inline.
 *
 * Inspiración: misma estética que /projects (cards). La vista de cliente
 * (`/clients/:id`) lista sus proyectos + permite editar email/notas y crear
 * proyecto pre-asignándole el clientId.
 */
export function ClientsPage() {
  const { data: clients, isLoading, error } = useClients();
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const create = useCreateClient();
  const [draftName, setDraftName] = useState('');

  const handleCreate = async () => {
    if (!draftName.trim()) return;
    const c = await create.mutateAsync({ name: draftName.trim() });
    setDraftName('');
    setShowCreate(false);
    navigate(`/clients/${c.id}`);
  };

  return (
    <div className="p-3 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Clientes</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="px-4 py-2 bg-vision-dark hover:bg-vision rounded-lg"
        >
          ＋ Nuevo cliente
        </button>
      </div>

      {isLoading && <div className="text-slate-400">Cargando…</div>}
      {error && <div className="text-red-400">Error: {(error as Error).message}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients?.map((c) => (
          <Link
            key={c.id}
            to={`/clients/${c.id}`}
            className="block p-4 bg-slate-900 border border-slate-800 hover:border-vision rounded-xl transition-colors"
          >
            <div className="font-medium text-slate-100">{c.name}</div>
            {c.contactEmail && (
              <div className="text-sm text-slate-400 mt-1">{c.contactEmail}</div>
            )}
            {c.notes && (
              <div className="text-sm text-slate-500 mt-2 line-clamp-2">{c.notes}</div>
            )}
          </Link>
        ))}
        {clients && clients.length === 0 && (
          <div className="col-span-full p-6 bg-slate-900 border border-dashed border-slate-700 rounded-xl text-slate-400 text-center">
            Sin clientes. Creá el primero para agrupar proyectos.
          </div>
        )}
      </div>

      {showCreate && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={() => setShowCreate(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              handleCreate();
            }}
            className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-md space-y-4"
          >
            <h2 className="text-xl font-semibold">Nuevo cliente</h2>
            <input
              autoFocus
              placeholder="Nombre del cliente"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2"
            />
            {create.error && (
              <div className="text-sm text-red-400">{(create.error as Error).message}</div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-3 py-2 text-slate-400"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={create.isPending || !draftName.trim()}
                className="px-4 py-2 bg-vision-dark hover:bg-vision rounded-lg disabled:opacity-50"
              >
                {create.isPending ? 'Creando…' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

/** Vista detalle del cliente: meta editable + proyectos del cliente. */
export function ClientDetailPage() {
  const { clientId } = useParams();
  const { data: client, isLoading, error } = useClient(clientId);
  const update = useUpdateClient();
  const archive = useArchiveClient();
  const navigate = useNavigate();
  const [showProjectModal, setShowProjectModal] = useState(false);

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [notes, setNotes] = useState('');

  const startEdit = () => {
    if (!client) return;
    setName(client.name);
    setContactEmail(client.contactEmail ?? '');
    setNotes(client.notes ?? '');
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!client) return;
    await update.mutateAsync({
      id: client.id,
      name: name.trim() || client.name,
      contactEmail: contactEmail.trim() || null,
      notes: notes.trim() || null,
    });
    setEditing(false);
  };

  if (isLoading) return <div className="p-6 text-slate-400">Cargando…</div>;
  if (error) return <div className="p-6 text-red-400">{(error as Error).message}</div>;
  if (!client) return null;

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <Link to="/clients" className="text-sm text-slate-400 hover:text-slate-200">
        ← Clientes
      </Link>

      <div className="mt-3 mb-6 bg-slate-900 border border-slate-700 rounded-xl p-5">
        {!editing ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold text-slate-100">{client.name}</h1>
                {client.contactEmail && (
                  <a
                    href={`mailto:${client.contactEmail}`}
                    className="text-sm text-vision hover:underline"
                  >
                    {client.contactEmail}
                  </a>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={startEdit}
                  className="px-3 py-1 text-xs bg-slate-800 hover:bg-slate-700 rounded"
                >
                  Editar
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`¿Archivar al cliente "${client.name}"? Sus proyectos quedan sin cliente asignado.`)) return;
                    await archive.mutateAsync(client.id);
                    navigate('/clients');
                  }}
                  className="px-3 py-1 text-xs bg-slate-800 hover:bg-red-500/30 hover:text-red-300 rounded"
                >
                  Archivar
                </button>
              </div>
            </div>
            {client.notes && (
              <p className="text-sm text-slate-400 mt-3 whitespace-pre-wrap">{client.notes}</p>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre"
              className="w-full bg-slate-950 border border-slate-700 rounded p-2"
            />
            <input
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="Email de contacto (opcional)"
              type="email"
              className="w-full bg-slate-950 border border-slate-700 rounded p-2"
            />
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notas (opcional)"
              className="w-full bg-slate-950 border border-slate-700 rounded p-2 h-24 resize-none"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(false)} className="px-3 py-2 text-slate-400">
                Cancelar
              </button>
              <button
                onClick={saveEdit}
                disabled={update.isPending}
                className="px-4 py-2 bg-vision-dark hover:bg-vision rounded-lg disabled:opacity-50"
              >
                {update.isPending ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg">Proyectos ({client.projects.length})</h2>
        <button
          onClick={() => setShowProjectModal(true)}
          className="px-3 py-1.5 bg-vision-dark hover:bg-vision rounded-lg text-sm"
        >
          ＋ Nuevo proyecto
        </button>
      </div>

      <div className="space-y-2">
        {client.projects.map((p) => (
          <Link
            key={p.id}
            to={`/projects/${p.slug}`}
            className="block p-3 bg-slate-900 border border-slate-800 hover:border-vision rounded-lg"
          >
            <div className="font-medium text-slate-100">{p.name}</div>
            {p.description && (
              <div className="text-sm text-slate-500 mt-1 line-clamp-2">{p.description}</div>
            )}
          </Link>
        ))}
        {client.projects.length === 0 && (
          <div className="p-4 bg-slate-900 border border-dashed border-slate-700 rounded-lg text-slate-500 text-center">
            Sin proyectos para este cliente.
          </div>
        )}
      </div>

      {showProjectModal && (
        <CreateProjectModal
          onClose={() => setShowProjectModal(false)}
          onCreated={(slug) => navigate(`/projects/${slug}`)}
          defaultClientId={client.id}
        />
      )}
    </div>
  );
}
